import { humanize } from '../../../../lib/formatLabels';
import { useQuery } from '@tanstack/react-query';
import { SchoolApi } from '../../../../api/client';
import { Badge, StatTile, StatGrid, TextLink, Term } from '../../../../components/ui';
import Spinner from '../../../../components/ui/Spinner';
import ErrorPanel from '../../../../components/ui/ErrorPanel';
import TrackRows, { type TrackRowItem } from '../../components/TrackRows';

interface Props { gradeId: number; sectionId?: number | 'all'; }

const SEV_TONE = { critical: 'danger', warning: 'warning', info: 'info' } as const;

export default function InterventionsTab({ gradeId, sectionId = 'all' }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics.interventions', gradeId, sectionId],
    queryFn: () => SchoolApi.analyticsInterventions({
      grade: gradeId,
      ...(sectionId !== 'all' ? { section: sectionId } : {}),
    }),
  });

  if (isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (isError || !data) return <ErrorPanel />;

  const categoryEntries = Object.entries(data.cases_by_category).map(([category, count]) => ({
    category,
    count,
  }));
  const categoryTotal = categoryEntries.reduce((n, e) => n + e.count, 0);
  const categoryRows: TrackRowItem[] = categoryEntries.map((e) => ({
    key: e.category,
    label: humanize(e.category),
    count: e.count,
    pct: categoryTotal > 0 ? (e.count / categoryTotal) * 100 : 0,
    ariaLabel: `${humanize(e.category)}: ${e.count} of ${categoryTotal} cases`,
  }));

  const severities = (['critical', 'warning', 'info'] as const);

  return (
    <>
      <StatGrid>
        <StatTile label="Open" value={data.open_cases} help="open_cases" />
        <StatTile label="In progress" value={data.in_progress_cases}
          help="Cases someone has picked up and is working on." />
        <StatTile label="Escalated" value={<span className="text-danger-ink">{data.escalated_cases}</span>} help="escalated_cases" tone="accent" />
        <StatTile label="Resolved · 30d" value={<span className="text-success">{data.resolved_30d}</span>} help="resolved_30d"
          sub={data.avg_time_to_resolve_hours != null ? `avg ${data.avg_time_to_resolve_hours}h to resolve` : undefined} />
      </StatGrid>
      <div className="mt-[22px] grid gap-[18px] min-[821px]:grid-cols-12">
        <div className="min-w-0 rounded-[16px] bg-panel p-[18px] min-[821px]:col-span-7">
          <h3 className="mb-2.5 font-display text-[15px] font-bold tracking-[-0.01em] text-ink"><Term k="cases_by_category">Cases by category</Term></h3>
          {categoryRows.length > 0
            ? <TrackRows rows={categoryRows} />
            : <p className="text-sm text-muted">No cases yet.</p>}
        </div>
        <div className="min-w-0 rounded-[16px] bg-panel p-[18px] min-[821px]:col-span-5">
          <div className="mb-1.5 flex items-center justify-between gap-3">
            <h3 className="font-display text-[15px] font-bold tracking-[-0.01em] text-ink"><Term k="alerts_by_severity">Open alerts by severity</Term></h3>
            <TextLink to="/alerts">Inbox →</TextLink>
          </div>
          <ul>
            {severities.map((sev) => (
              <li key={sev} className="flex items-center justify-between border-b border-hairline py-3 last:border-b-0">
                <Badge tone={SEV_TONE[sev]}>{humanize(sev)}</Badge>
                <span className="font-mono font-semibold tabular-nums">{data.open_alerts_by_severity[sev]}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}
